import React, { useEffect, useState } from "react";

import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import * as yup from "yup";

import UITabMaterial, { PanelProps } from "../../../../../../../components/Generic/UITabMaterial";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Subcontractor } from "../../../../../../../models/subcontractor";
import { BreadcrumbKit } from "../Breadcrumb";
import { useSubcontractorActions } from "../hooks/useActions";
import { SubcontractorProvider, useSubcontractorContext } from "./context/Subcontractor";
import Details from "./Details";
import Equipments from "./Equipments";
import OrderButtons from "./FooterButtons";
import { EditSubConstractorLoading } from "./Loading";
import Member from "./Member";
import { Orders } from "./Orders";
import useStyles from "./styles";

type Param = {
  subcontractorId?: string | undefined;
};

type LocationState = {
  viewOnly?: boolean;
};

const EditSubConstractor = () => {
  const { subcontractorId } = useParams<Param>();
  const routeLocation = useLocation<LocationState>();
  const viewOnly = !!routeLocation?.state?.viewOnly;

  const { subcontractor, setSubcontractor } = useSubcontractorContext();
  const { getSubcontractorById, useSaveSubcontractor, onBackToSubcontractorsTab } =
    useSubcontractorActions();
  const { data: subcontractorData, isFetching: loadingSubcontractor } = getSubcontractorById(
    subcontractorId ? Number(subcontractorId) : undefined
  );

  useEffect(() => {
    if (subcontractorData) {
      setSubcontractor((prev) => ({
        ...prev,
        ...subcontractorData,
      }));
    }
  }, [subcontractorData]);

  const { mutate: saveSubcontractor, isLoading: isLoadingSaveSubcontractor } =
    useSaveSubcontractor(onBackToSubcontractorsTab);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { someFieldHasError } = useYupValidation(errors);
  const { t: translate } = useTranslation();

  const classes = useStyles();

  const detailsPanel: PanelProps = {
    menuItem: translate("details"),
    className: classes.uiTab,
    key: "details",
    loading: loadingSubcontractor,
    skeleton: () => <EditSubConstractorLoading />,
    hasError: someFieldHasError(["businessName", "businessEmail", "businessLocation"] as Array<
      keyof Subcontractor
    >),
    render: () => <Details errors={errors} isView={viewOnly} />,
  };
  const memberPanel: PanelProps = {
    menuItem: translate("member"),
    className: classes.uiTab,
    key: "member",
    loading: loadingSubcontractor,
    skeleton: () => <EditSubConstractorLoading />,
    render: () => <Member isView={viewOnly} />,
  };
  const ordersPanel: PanelProps = {
    menuItem: translate("orders"),
    className: classes.uiTab,
    key: "orders",
    loading: loadingSubcontractor,
    skeleton: () => <EditSubConstractorLoading />,
    render: () => <Orders />,
  };
  const equipmentsPanel: PanelProps = {
    menuItem: translate("equipments"),
    className: classes.uiTab,
    key: "equipments",
    loading: loadingSubcontractor,
    skeleton: () => <EditSubConstractorLoading />,
    render: () => <Equipments isView={viewOnly} />,
  };

  const leftPanels: PanelProps[] = [detailsPanel, memberPanel, ordersPanel, equipmentsPanel];

  const schema = yup.object().shape({
    businessName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    businessEmail: yup
      .string()
      .email()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    businessLocation: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const onValidate = () => {
    try {
      schema.validateSync(
        { ...subcontractor, businessLocation: subcontractor?.businessLocation?.longAddress },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const onSubmit = () => {
    const isValid = onValidate();

    if (isValid) {
      saveSubcontractor({
        data: subcontractor,
        id: subcontractor.id || Number(subcontractorId),
      });
    }
  };

  return (
    <Box className={classes.Wrapper}>
      <BreadcrumbKit
        items={[
          {
            key: "edit",
            label: `${translate(viewOnly ? "view" : "edit")} ${subcontractor?.businessName ?? ""}`,
          },
        ]}
      />
      <div className={classes.ordersTab}>
        <UITabMaterial
          leftPanels={leftPanels}
          activityPanels={leftPanels}
          showRightPanel={false}
          showActivityButton={false}
          tabFooter={
            <OrderButtons
              isLoading={isLoadingSaveSubcontractor}
              onCancel={onBackToSubcontractorsTab}
              onSave={onSubmit}
              isShowSave={!viewOnly}
            />
          }
        />
      </div>
    </Box>
  );
};

const SubcontractorEditMaintenance = () => {
  return (
    <SubcontractorProvider>
      <EditSubConstractor />
    </SubcontractorProvider>
  );
};

export default SubcontractorEditMaintenance;
